/**
 * TempPasswordModal — shows the one-time admin-generated temporary password
 * for a user after a reset. Extracted from AdminDashboard.jsx (Jun 4 2026).
 */
import React from 'react';
import { KeyRound, Check, Copy } from 'lucide-react';
import { Button } from './ui/button';

const TempPasswordModal = ({
  resetPasswordUser,
  tempPassword,
  passwordCopied,
  onCopy,
  onClose,
}) => {
  if (!resetPasswordUser) return null;
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="admin-temp-password-modal"
    >
      <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full border border-slate-700 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Temporary password generated</h3>
            <p className="text-xs text-gray-400">
              For <span className="text-white font-medium">{resetPasswordUser}</span>
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-300 mb-3">
          Copy this password and share it securely with the user. They'll be prompted to change it after logging in.
        </p>

        <div className="p-4 bg-slate-900/60 border-2 border-dashed border-indigo-500/40 rounded-lg mb-3">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Temporary password</p>
          <div className="flex items-center gap-2">
            <code
              id="admin-temp-password-value"
              data-testid="admin-temp-password-value"
              className="flex-1 text-xl font-mono font-bold text-indigo-300 tracking-widest select-all break-all"
            >
              {tempPassword}
            </code>
            <Button
              onClick={onCopy}
              size="sm"
              variant="outline"
              className="shrink-0 border-indigo-500 text-indigo-300 hover:bg-indigo-500 hover:text-white"
              data-testid="admin-temp-password-copy"
            >
              {passwordCopied ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg mb-4">
          <p className="text-xs text-amber-200 leading-relaxed">
            <strong>Security notice:</strong> This password is shown only once. If you close this dialog without copying it, you'll need to reset it again.
          </p>
        </div>

        <Button
          onClick={onClose}
          className="w-full bg-slate-700 hover:bg-slate-600"
          data-testid="admin-temp-password-close"
        >
          Close
        </Button>
      </div>
    </div>
  );
};

export default TempPasswordModal;
